import { ToggleButton, ToggleButtonProps } from "@mui/material";
import { motion } from "motion/react";
import { ReactElement } from "react";
import styled from "styled-components";

import { PNG } from "@ryde/assets";
import {
  DATA_E2E_KEY,
  IStyledAttributes,
  TStationRouteStatisticsLabelTone,
} from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  indigoMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  rydeColors,
  ScreenDepth,
  tealMui,
} from "@ryde/theme";

const statisticsLabelToneColor: Record<
  TStationRouteStatisticsLabelTone,
  string
> = {
  rides: blueGreyMui._800,
  ridesCreated: tealMui._800,
  productions: indigoMui._800,
};

type TStationRouteTabsToggleButtonProps = Omit<ToggleButtonProps, "size">;

export const StationRouteTabsToggleButton = ({
  ...props
}: TStationRouteTabsToggleButtonProps): ReactElement => {
  return (
    <ToggleButton {...props} size="small" sx={{ width: 100, height: 32 }} />
  );
};

/** Grid columns are driven by motion style (gridTemplateColumns) for smooth resize. */
export const StationRouteDrawerWrapper = styled(
  motion.div,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "stationRoute-drawer-wrapper",
})`
  display: grid;
  grid-template-areas:
    "header    header     header     header"
    "lhandle   form       mhandle    map"
    "lhandle   footer     mhandle    map";
  grid-template-rows: 67px 1fr 64px;
  height: 100%;

  .StationRouteDrawer-Header {
    grid-area: header;
  }

  .StationRouteDrawer-Form {
    grid-area: form;
  }

  .StationRouteDrawer-Footer {
    grid-area: footer;
  }

  .StationRouteDrawer-Map {
    grid-area: map;
  }

  .StationRouteDrawer-LeftHandle,
  .StationRouteDrawer-MiddleHandle {
    background-color: ${greyMui._300};
    cursor: col-resize;
    user-select: none;
    z-index: ${ScreenDepth.GRID_SPLITTER};
    position: relative;

    &:hover {
      background-color: ${greyMui._400};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._500};
      outline-offset: -2px;
    }
  }

  .StationRouteDrawer-LeftHandle {
    grid-area: lhandle;
  }

  .StationRouteDrawer-MiddleHandle {
    grid-area: mhandle;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    flex-direction: column;

    .StationRouteDrawer-LeftHandle,
    .StationRouteDrawer-MiddleHandle {
      display: none;
    }
  }
`;

export const StationRouteLayoutWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-layout-wrapper",
  },
)`
  display: grid;
  grid-template-columns: 720px 1fr;
  width: 100%;
  height: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

export const StationRouteTabsWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-tabs-wrapper",
  },
)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: auto;
`;

export const StationRouteTabsEarsContainer = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "station-route-tabs-ears-container",
  },
)`
  padding-top: 12px;
  padding-bottom: 12px;
  padding-inline-start: 22px;
  transition: filter 0.45s ease-out;

  transition:
    filter 0.55s ease-out,
    backdrop-filter 0.55s ease-out;
  &.inner-item-editing {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
  }
`;

export const StationRouteTab1Content = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "stationRoute-tab1-content",
})`
  padding-inline-start: 22px;
  margin-top: 12px;
`;

export const StationRouteTabsErrorContainer = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-tabs-errors-container",
  },
)`
  padding-inline-end: 18px;
`;

export const StationRouteTabsTabStationsStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-tab-stations",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 12px;

  &.inner-item-editing [data-e2e="station-route-station-panel"] {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
    transition:
      filter 0.55s ease-out,
      backdrop-filter 0.55s ease-out;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
    width: 100%;
  }
`;

export const StationRouteStationsTabContentStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-stations-tab-content",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
  padding-inline-start: 5px;
  width: 85%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: calc(100% - 10px);
    padding-inline-start: 12px;
  }
`;

export const StationRouteTabsContentWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-tab-content",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
  padding-inline-start: 22px;
  padding-inline-end: 20px;
  margin-top: 14px;
  width: 85%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: calc(100% - 30px);
    padding-inline-start: 12px;
  }
`;

export const StationRouteBasicTabInputs = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "stationRoute-basic-tab-inputs",
})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 15px;
  margin-bottom: 20px;
  .template_wrapper:nth-child(3) {
    grid-column: 1 / 3;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    row-gap: 15px;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr;
  }
`;

export const StationRouteMapWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-map-wrapper",
  },
)`
  height: 100%;
  width: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 10px;
  }
`;

export const StationRouteMapStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "stationRoute-map",
})`
  height: calc(100vh - 62px);
  position: sticky;
  top: 62px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: calc(100vh - 232px);
  }
`;

export const StationRouteTabsBottomBlocksStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-tabs-bottom",
  },
)`
  margin-top: auto;
  padding-bottom: 14px;
  padding-inline-start: 22px;

  .link_clickable {
    cursor: pointer;
    color: ${blueMui._700};
    font-weight: 600;
    text-decoration: underline;
    &:hover {
      color: ${blueMui._900};
    }
  }
`;

export const StationRouteBottomBlockStyled = styled.span.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-bottom-block",
  },
)`
  color: ${blueGreyMui._500};
  display: inline-flex;
  font-size: 12px;
  padding-inline-end: 7px;
  gap: 4px;
  align-items: baseline;

  label {
    color: ${greyMui._500};
  }
  span {
    color: ${blueGreyMui._700};
    font-weight: 500;
  }

  .station-route-bottom-optimize-icon {
    margin-inline-end: 2px;
  }

  &:not(&:last-child) {
    &:after {
      content: "";
      border-inline-end: 1px solid ${blueGreyMui._500};
      height: 8px;
      margin-inline-start: 8px;
      display: inline-block;
    }
  }
`;

export const StationRouteStatisticsTabStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-statistics-tab",
  },
)`
  padding: 16px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .link_clickable {
    cursor: pointer;
    color: ${blueMui._700};
    font-weight: 600;
    text-decoration: underline;
    &:hover {
      color: ${blueMui._900};
    }
  }
`;

export const StationRouteStatisticsInfoStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-statistics-info",
    role: "status",
  },
)`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding-block: 10px;
  padding-inline: 16px 12px;
  border-radius: 8px;
  background-color: ${blueMui._50};
  border: 1px solid ${blueMui._100};
  color: ${blueMui._800};
  font-size: 13px;
  line-height: 1.45;

  .statistics-info-icon {
    flex-shrink: 0;
    margin-top: 1px;
    color: ${blueMui._600};
    display: inline-flex;
  }

  .statistics-info-text {
    flex: 1;
    min-width: 0;
  }

  .statistics-info-refresh {
    flex-shrink: 0;
    margin-inline-start: 4px;
    margin-top: -4px;
    margin-bottom: -4px;

    .MuiIconButton-root {
      color: ${blueMui._600};
    }
  }
`;

export const StationRouteStatisticsPlateStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-statistics-plate",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 10px;
  background-color: ${greyMui._100};
  border: 1px solid ${greyMui._200};
`;

export const StationRouteStatisticsLineStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-statistics-line",
  },
)<{ $labelTone?: TStationRouteStatisticsLabelTone }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;

  label {
    color: ${({ $labelTone }) =>
      ($labelTone && statisticsLabelToneColor[$labelTone]) || blueGreyMui._800};
    font-weight: 700;
  }

  span {
    color: ${blueGreyMui._800};
    font-weight: 600;
  }
`;

export const StationRouteMatchingMlPathsStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-matching-ml-paths-wrap",
  },
)`
  margin-top: 8px;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${blueGreyMui._700};
  font-size: 12px;
`;

export const StationRouteMatchingMlPathHeaderStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-matching-ml-paths-header",
  },
)`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;

  label {
    color: ${greyMui._500};
  }

  span {
    color: ${blueGreyMui._700};
    font-weight: 500;
  }
`;

export const StationRouteMatchingMlPathExpandToggleStyled = styled.button.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-matching-ml-paths-expand",
    type: "button",
  },
)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${greyMui._500};
  line-height: 0;
  border-radius: 2px;
  flex-shrink: 0;
  vertical-align: baseline;

  &:hover {
    color: ${blueGreyMui._700};
  }

  &:focus-visible {
    outline: 2px solid ${blueGreyMui._400};
    outline-offset: 1px;
  }

  svg {
    width: 10px;
    height: 10px;
    opacity: 0.85;
  }

  &:hover svg {
    opacity: 1;
  }
`;

export const StationRouteEditTopInfoContainerStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "stationRoute-edit-top-info-container",
  },
)`
  .info_item {
    padding-inline-start: 22px;
    padding-inline-end: 25px;
    padding-top: 5px;
    padding-bottom: 5px;
    &:first-child {
      padding-top: 10px;
    }

    p {
      font-size: 14px;
      font-weight: 500;
      padding: 5px;
      color: ${orangeMui._800};
      background-color: ${orangeMui._50};
      outline: 1px solid ${orangeMui._100};
      border-radius: 4px;
    }
  }
`;

export const StationRouteHtmlInfoContainerStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-banner",
  },
)`
  display: grid;
  grid-template-columns: 1fr 350px;
  height: 180px;
  gap: 20px;
  padding-inline-start: 22px;
  padding-inline-end: 22px;
  margin-top: 7px;
  background-color: ${rydeColors.searchFilterFieldBackgroundError};
  border-radius: 4px;
  background-image: url(${PNG.SavedPathBannerPNG});
  background-repeat: no-repeat;
  background-position: bottom 0 right 25px;
  &.is-customized {
    outline: 1px solid ${rydeColors.yellowButton};
  }
  .banner__info {
    padding: 4px;
  }
  .banner__info-title {
    font-size: 14px;
    color: ${rydeColors.stationRouteBannerText};
    margin-bottom: 5px;
  }
  .banner__info-text {
    color: ${rydeColors.stationRouteBannerText};
    font-size: 13px;
    line-height: 1.5;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 12px;
    padding-inline-end: 12px;
    width: 100%;
    .banner__image {
      background-image: none;
    }
  }
`;
